import React from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HardwareIcon from '@mui/icons-material/Hardware';

interface SetupSectionCardProps {
    title: string;
    subtitle: React.ReactNode;
    headerEnd?: React.ReactNode;
    children: React.ReactNode;
}

export const SetupSectionCard: React.FC<SetupSectionCardProps> = ({
    title,
    subtitle,
    headerEnd,
    children
}) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                </Box>
                {headerEnd}
            </Stack>
            {children}
        </Stack>
    </Paper>
);

interface StatusProps {
    configured: boolean;
}
export const StatusChip: React.FC<StatusProps> = ({ configured }) =>
    configured ? (
        <Chip label="Configured" color="success" size="small" sx={{ fontWeight: 600 }} />
    ) : (
        <Chip label="Not set" size="small" sx={{ fontWeight: 600 }} />
    );

export const ReadOnlyStatus: React.FC<StatusProps> = ({ configured }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        {configured ? (
            <CheckIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
            <CloseIcon color="error" sx={{ fontSize: 18 }} />
        )}
        <Typography variant="body2" fontWeight={600} color={configured ? 'success.main' : 'error.main'}>
            {configured ? 'Configured' : 'Not set'}
        </Typography>
    </Stack>
);

interface FieldStatusRowProps {
    label: string;
    isSet: boolean;
}

export const FieldStatusRow: React.FC<FieldStatusRowProps> = ({ label, isSet }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" py={1}>
        <Typography variant="body2">{label}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
            {isSet ? (
                <CheckIcon color="success" sx={{ fontSize: 16 }} />
            ) : (
                <CloseIcon color="error" sx={{ fontSize: 16 }} />
            )}
            <Typography variant="body2" fontWeight={600} color={isSet ? 'success.main' : 'error.main'}>
                {isSet ? 'Set' : 'Not set'}
            </Typography>
        </Stack>
    </Stack>
);

interface LabeledFieldProps {
    label: string;
    buildTime?: boolean;
    children: React.ReactNode;
}

/** Renders a field label (with an optional BUILD-TIME badge) above the field control. */
export const LabeledField: React.FC<LabeledFieldProps> = ({ label, buildTime, children }) => (
    <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            {buildTime && (
                <Tooltip title="Build time setting">
                    <HardwareIcon sx={{ fontSize: 12 }} />
                </Tooltip>
            )}
        </Stack>
        {children}
    </Stack>
);
